import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiCalendar, FiMapPin, FiActivity, FiAward, FiInfo } = FiIcons;

const InfoPill = ({ icon, label, value }) => (
  <div className="flex flex-col gap-1 p-3 border border-[#658d71] bg-[rgba(0,0,0,0.15)]">
    <div className="flex items-center gap-2">
      <SafeIcon icon={icon} size={12} className="text-[#658d71]" />
      <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-black">{label}</span>
    </div>
    <span className="text-sm font-black text-white uppercase truncate">{value || '--'}</span>
  </div>
);

const PlayerMetricsBio = ({ player }) => {
  if (!player) return null;

  const birthPlace = [player.birthCity, player.birthStateProvince, player.birthCountry].filter(Boolean).join(', ');
  const debut = player.mlbDebutDate ? new Date(player.mlbDebutDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '--';

  return (
    <div className="flex flex-col gap-4">
      {/* Hero Header */}
      <div className="relative overflow-hidden border-2 border-[#658d71] bg-gradient-to-br from-[#386646] to-[#1e3a29] p-6">
        <div className="absolute top-0 right-0 p-4 opacity-5">
          <SafeIcon icon={FiUser} size={120} />
        </div>
        
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6">
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[#1e3a29] border-4 border-[#d8ceb3] flex items-center justify-center shrink-0 shadow-2xl">
            <SafeIcon icon={FiUser} size={48} className="text-[#d8ceb3]" />
          </div>
          
          <div className="flex flex-col items-center sm:items-start gap-2">
            <div className="flex items-center gap-3">
              <span className="bg-[#e85555] text-white text-xs font-black px-2 py-1">
                #{player.primaryNumber || '--'}
              </span>
              <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tighter text-white">
                {player.fullName}
              </h2>
            </div>
            
            <div className="flex flex-wrap justify-center sm:justify-start gap-3">
              <span className="text-[#d8ceb3] text-sm font-bold uppercase tracking-[0.2em] border-b-2 border-[#d8ceb3]">
                {player.primaryPosition?.name || '--'}
              </span>
              <span className="text-[#a9c9b1] text-sm font-medium uppercase tracking-widest">
                {player.currentTeam?.name || 'Boston Red Sox'}
              </span>
            </div>

            <div className="flex gap-2 mt-2">
              <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest ${player.active ? 'bg-[#658d71] text-white' : 'bg-[#e85555] text-white'}`}>
                {player.active ? 'Active Roster' : 'Inactive'}
              </span>
              <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest bg-[rgba(216,206,179,0.2)] text-[#d8ceb3] border border-[#d8ceb3]">
                {player.pitchHand?.description} / {player.batSide?.description}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scouting Profile Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <InfoPill icon={FiCalendar} label="Age" value={`${player.currentAge || '--'} Years`} />
        <InfoPill icon={FiActivity} label="Height" value={player.height} />
        <InfoPill icon={FiActivity} label="Weight" value={`${player.weight} lbs`} />
        <InfoPill icon={FiMapPin} label="Origin" value={player.birthCountry} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#658d71] p-4 bg-[rgba(0,0,0,0.1)]">
          <div className="flex items-center gap-2 mb-3">
            <SafeIcon icon={FiAward} size={14} className="text-[#d8ceb3]" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#d8ceb3]">Career Background</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between border-b border-[rgba(101,141,113,0.2)] pb-1">
              <span className="text-[10px] text-[#658d71] uppercase font-bold">MLB Debut</span>
              <span className="text-[10px] text-white font-black">{debut}</span>
            </div>
            <div className="flex justify-between border-b border-[rgba(101,141,113,0.2)] pb-1">
              <span className="text-[10px] text-[#658d71] uppercase font-bold">Draft Info</span>
              <span className="text-[10px] text-white font-black">{player.draftYear || 'Free Agent'}</span>
            </div>
            <div className="flex justify-between border-b border-[rgba(101,141,113,0.2)] pb-1">
              <span className="text-[10px] text-[#658d71] uppercase font-bold">Birthplace</span>
              <span className="text-[10px] text-white font-black truncate ml-4">{birthPlace}</span>
            </div>
          </div>
        </div>

        <div className="border border-[#658d71] p-4 bg-[rgba(0,0,0,0.1)]">
          <div className="flex items-center gap-2 mb-3">
            <SafeIcon icon={FiInfo} size={14} className="text-[#d8ceb3]" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#d8ceb3]">Physical Profile</span>
          </div>
          <p className="text-[11px] text-[#a9c9b1] leading-relaxed italic">
            {player.fullName} is a {player.currentAge}-year-old {player.primaryPosition?.name?.toLowerCase()} 
            standing {player.height} and weighing {player.weight} lbs. 
            He {player.batSide?.code === 'R' ? 'bats right' : player.batSide?.code === 'L' ? 'bats left' : 'is a switch hitter'} 
            and {player.pitchHand?.code === 'R' ? 'throws right' : 'throws left'}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlayerMetricsBio;